import { useState } from 'react'
import { X, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface CompletionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  requestId: string
  equipmentDescription?: string
  problemDescription?: string
  estimatedCost?: number
  actualCost?: number
  urgency?: string
  hasAttachments?: boolean
  isPurchase?: boolean
}

export default function CompletionModal({
  isOpen,
  onClose,
  onConfirm,
  requestId,
  equipmentDescription,
  problemDescription,
  estimatedCost,
  actualCost,
  urgency,
  hasAttachments = false,
  isPurchase = false
}: CompletionModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    try {
      setLoading(true)
      setError(null)
      
      await onConfirm()
      
      toast.success(isPurchase ? 'Compra registada com sucesso!' : 'Execução registada com sucesso!', {
        duration: 3000,
        position: 'top-right',
      })
      
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : isPurchase ? 'Erro ao registar compra' : 'Erro ao registar execução')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  const getUrgencyColor = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'bg-gray-100 text-gray-800'
      case 'media':
        return 'bg-yellow-100 text-yellow-800'
      case 'alta':
        return 'bg-orange-100 text-orange-800'
      case 'critica':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getUrgencyLabel = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'Baixa'
      case 'media':
        return 'Média'
      case 'alta':
        return 'Alta'
      case 'critica':
        return 'Crítica'
      default:
        return 'N/A'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-brand-primary-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                {isPurchase ? 'Confirmar Registo de Compra' : 'Confirmar Registo de Execução'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Alerta sobre irreversibilidade */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Atenção: Ação Irreversível
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {isPurchase
                      ? 'Após registar esta compra, não será possível editá-la novamente.'
                      : 'Após registar esta execução, não será possível editá-la novamente.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Informações da solicitação */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-2">
                {isPurchase ? 'Detalhes da Compra:' : 'Detalhes da Manutenção:'}
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-brand-text-secondary">ID:</span>
                  <span className="font-medium text-brand-text-primary">
                    {requestId?.substring(0, 8)}...
                  </span>
                </div>
                {equipmentDescription && (
                  <div className="flex justify-between">
                    <span className="text-brand-text-secondary">Equipamento:</span>
                    <span className="font-medium text-brand-text-primary truncate ml-2">
                      {equipmentDescription}
                    </span>
                  </div>
                )}
                {problemDescription && (
                  <div>
                    <span className="text-brand-text-secondary">Problema:</span>
                    <p className="text-brand-text-primary mt-1 truncate">
                      {problemDescription}
                    </p>
                  </div>
                )}
                {urgency && (
                  <div className="flex justify-between items-center">
                    <span className="text-brand-text-secondary">Urgência:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyColor(urgency)}`}>
                      {getUrgencyLabel(urgency)}
                    </span>
                  </div>
                )}
                {(estimatedCost || actualCost) && (
                  <div className="flex justify-between">
                    <span className="text-brand-text-secondary">Custo:</span>
                    <span className="font-medium text-brand-text-primary">
                      {actualCost ? `€${actualCost.toFixed(2)}` : `€${estimatedCost?.toFixed(2) || 'N/A'}`}
                      {actualCost && estimatedCost && actualCost !== estimatedCost && (
                        <span className="text-xs text-gray-500 ml-1">
                          (estimado: €{estimatedCost.toFixed(2)})
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {hasAttachments && (
                  <div className="flex justify-between">
                    <span className="text-brand-text-secondary">Anexos:</span>
                    <span className="font-medium text-brand-text-primary">
                      Sim
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Pergunta de confirmação */}
            <div className="text-sm text-brand-text-secondary">
              <p>{isPurchase
                ? 'Tem certeza de que deseja registar esta compra?'
                : 'Tem certeza de que deseja registar esta execução de manutenção?'}</p>
            </div>

            {/* Erro */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-primary-500 border border-transparent rounded-md hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    A registar...
                  </div>
                ) : (
                  isPurchase ? 'Confirmar Compra' : 'Confirmar Execução'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}